import { ReactElement, useContext, useEffect, useState } from 'react';

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import CssBaseline from '@mui/material/CssBaseline';

import routes, { IRouteProps } from 'routes/routes';

import { alpha, Box, ThemeProvider } from '@mui/material';
import SidebarComponent from './components/SidebarComponent';
import DefaultLayout from './components/DefaultLayout';
import { createTheme } from '@mui/material/styles';
import { AppContext } from './context/AppContextProvider';
import { backgroundColorDarkTheme } from './styles/global.styles';
import { AuthenticationProvider } from 'pages/AuthenticationPages/context/AuthenticationContextProvider';
import { api, getToken } from './services/DefaultService';
import { AxiosError } from 'axios';
import { IApiErrorData } from './services/types';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pt-br';
import { ptBR } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import HeaderComponent from 'components/HeaderComponent';
import MapView from './components/MapComponent/MapView';
import "leaflet/dist/leaflet.css";

const theme = createTheme({
  palette: {
    background: {
      default: '#0b1220',
      paper: '#ffffff',
    },
    text: {
      primary: '#ffffff',
      secondary: '#8c9ead',
      disabled: alpha('#ffffff', 0.6),
    },
    primary: {
      main: '#1976d2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#516f7f',
      light: '#8c9ead',
      contrastText: '#ffffff',
    },
    error: {
      main: '#c40000',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#c48e19',
      contrastText: '#ffffff',
    },
    info: {
      main: '#ffffff',
      contrastText: '#1a2035',
    },
    success: {
      main: '#149b36',
      contrastText: '#ffffff',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: backgroundColorDarkTheme,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
              WebkitTextFillColor: '#000 !important',
              backgroundColor: 'transparent !important',
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-focused': {
            color: '#1976d2',
          },
          '&.Mui-disabled': {
            color: alpha('#ffffff', 0.6),
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',

          '&::before': {
            borderColor: '#516f7f',
          },

          '&:hover:not(.Mui-disabled)::before': {
            borderColor: '#8c9ead',
          },

          '&::after': {
            borderColor: '#1976d2',
          },

          '&.Mui-disabled': {
            color: alpha('#516f7f', 0.2),
            '&::before': {
              borderBottomStyle: 'dotted',
              borderColor: alpha('#516f7f', 0.4),
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#516f7f',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#8c9ead',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
          },
          '&.Mui-disabled': {
            backgroundColor: alpha('#516f7f', 0.2),
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha('#516f7f', 0.4),
            },
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#333',
          },
          '&.Mui-selected': {
            backgroundColor: '#444',
            '&:hover': {
              backgroundColor: '#555',
            },
          },
        },
      },
    },
  },
});

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('pt-br');

export default function App() {
  const [isLogged, setIsLogged] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();
  const { setIsShowLoadingComponent, showSnackbar, isRouteAllowed } =
    useContext(AppContext);
  const { pathname } = useLocation();

  const getRoutes = (
    allRoutes: IRouteProps[],
  ): ReactElement[] | ReactElement | [] =>
    allRoutes.flatMap((route: IRouteProps) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const element = route.component;
        return <Route path={route.route} element={element} key={route.key} />;
      }

      return [];
    });

  const checkCurrentRoute = (routes: IRouteProps[], isLogged: boolean) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].route === pathname) {
        if (routes[i].route === pathname && routes[i].protected && !isLogged) {
          navigate('/app/authentication/signIn');
        } else if (!routes[i].protected && isLogged) {
          let route = routes.find((r) => isRouteAllowed(r.permissionId));

          if (route?.collapse && route.collapse.length > 0) {
            route = route.collapse.find((r) => isRouteAllowed(r.permissionId));
          }
          if (route && route.route) navigate(route.route);
        }
        break;
      }
      if (routes[i].collapse && routes[i].collapse!.length > 0) {
        checkCurrentRoute(routes[i].collapse!, isLogged);
      }
    }
  };

  const routePath = (routes: IRouteProps[]) => {
    let path = '';
    routes.forEach((route) => {
      if (isRouteAllowed(route.permissionId)) {
        path = route.route ?? '';
        return;
      } else if (route.collapse && route.collapse?.length > 0) {
        path = routePath(route.collapse);
        return;
      }
    });
    return path;
  };

  const checkIsLoggedUser = () => {
    if (sessionStorage.getItem('token') == null) {
      setIsLogged(false);
      checkCurrentRoute(routes, false);
    } else {
      api.defaults.headers.common['Authorization'] =
        'Bearer ' + window.atob(sessionStorage.getItem('token')!);
      checkCurrentRoute(routes, true);
      setIsLogged(true);
    }
    setIsLoaded(true);
  };

  const handleErrors = (error: AxiosError<any>) => {
    if (error.response && error.response.data) {
      const message =
        error.response.data.userMessage || 'Erro interno do servidor!';
      switch (error.response.status) {
        case 0: {
          showSnackbar(
            'Falha ao conectar-se ao servidor. Verifique sua conexão Wi-Fi!',
            'warning',
          );
          break;
        }
        case 401: {
          showSnackbar(message, 'warning');
          sessionStorage.removeItem('token');
          setTimeout(() => {
            window.location.href = '/app/authentication/sign-in';
          }, 500);
          break;
        }
        default: {
          showSnackbar(message, 'error');
        }
      }
    }
  };

  useEffect(() => {
    api.interceptors.request.use(
      (config) => {
        const token = getToken();
        if (token)
          config.headers.Authorization = `Bearer ${window.atob(token)}`;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    api.interceptors.response.use(
      (config) => {
        return config;
      },
      (error) => {
        if (!error?.config?._suppressError) {
          handleErrors(error as AxiosError<IApiErrorData>);
        }
        return Promise.reject(error);
      },
    );
  }, []);

  useEffect(() => {
    checkIsLoggedUser();
  }, [pathname]);

  if (!isLoaded) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <AuthenticationProvider>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={'pt-br'}
          localeText={
            ptBR.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <CssBaseline />
          {isLogged ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              {window.innerWidth < 900 && <HeaderComponent routes={routes} />}
              <Box sx={{ display: 'flex', height: '100%' }}>
                {window.innerWidth > 900 && (
                  <SidebarComponent brandName="AlertAi" routes={routes} />
                )}
              </Box>
              <DefaultLayout>
                <Routes>{getRoutes(routes)}</Routes>
              </DefaultLayout>
            </Box>
          ) : (
            <Routes>
              {getRoutes(routes)}
              <Route path="/map" element={<MapView selectedId={null} sidebarOpen={false} selectedIds={[]} />} />

              <Route
                path="*"
                element={<Navigate to="app/authentication/signIn" />}
              />
            </Routes>
          )}
        </LocalizationProvider>
      </AuthenticationProvider>
    </ThemeProvider>
  );
}
